import { useEffect } from "preact/compat";
import { ensureExhaustive } from "@dhau/lang-extras";
import useAppDispatch from "~/hooks/use-app-dispatch.ts";
import useAppSelector from "~/hooks/use-app-selector.ts";
import {
	guestLogin,
	setWhoFor,
	setGiftType,
	setGiftChooser,
	clearGiftType,
	clearGiftChooser,
	clearParsedNameValues,
	clearWhoFor,
	changeEmailAddress,
} from "~/features/user/store-slice.ts";
import { selectUser } from "~/features/user/selectors.ts";
import { useWebsiteUrl } from "~/context/website-url.tsx";
import PurposeSelectionStep from "./purpose-selection-step.tsx";
import GiftChooserSelectionStep from "./gift-chooser-selection-step.tsx";
import GiftTypeSelectionStep from "./gift-type-selection-step.tsx";
import NameCollectionStep from "./name-collection-step.tsx";
import EmailCollectionStep from "./email-collection-step.tsx";
import BackButton from "./back-button.tsx";
import type {
	WhoFor,
	GiftType,
} from "@brickme/project-core/src/model/project.ts";

type GetStartedStep = "purpose" | "giftChooser" | "giftType" | "name" | "email";

type GetStartedFlowProps = {
	readonly onGuestLogin: () => void;
	readonly skipNameAndEmail?: boolean;
	readonly onComplete?: () => void;
};

function GetStartedFlow({
	onGuestLogin,
	skipNameAndEmail,
	onComplete,
}: GetStartedFlowProps) {
	const dispatch = useAppDispatch();
	const user = useAppSelector(selectUser);
	const websiteUrl = useWebsiteUrl("");

	// Get flow state from Redux
	const whoFor = useAppSelector((state) => state.user.whoFor);
	const giftChooser = useAppSelector((state) => state.user.giftChooser);
	const giftType = useAppSelector((state) => state.user.giftType);
	const parsedNameValues = useAppSelector(
		(state) => state.user.parsedNameValues,
	);

	// Determine current step based on Redux state
	const getCurrentStep = (): GetStartedStep => {
		if (
			!skipNameAndEmail &&
			(user.type === "waitingForOtp" || user.type === "verifyingOtp")
		) {
			return "email";
		}
		if (!skipNameAndEmail && parsedNameValues) {
			return "email";
		}
		if (giftType && !parsedNameValues && !skipNameAndEmail) {
			return "name";
		}
		if (whoFor === "someone_else" && !giftChooser) {
			return "giftChooser";
		}
		if (whoFor === "someone_else" && giftChooser === "recipient" && !giftType) {
			return "giftType";
		}
		if (whoFor === "myself" && !parsedNameValues && !skipNameAndEmail) {
			return "name";
		}
		return "purpose";
	};

	// Detect flow completion
	useEffect(() => {
		// Centralized completion logic
		const isFlowComplete = () => {
			if (skipNameAndEmail) {
				if (whoFor === "myself") return true;
				if (whoFor === "someone_else" && giftChooser === "myself") return true;
				if (
					whoFor === "someone_else" &&
					giftChooser === "recipient" &&
					giftType === "brick_mosaic"
				)
					return true;
				// gift card and brick mosaic handled by redirect
			} else {
				return user.type === "guest" || user.type === "signedIn";
			}
			return false;
		};

		if (isFlowComplete()) {
			onComplete?.();
		}
	}, [skipNameAndEmail, whoFor, giftChooser, giftType, user.type, onComplete]);

	const currentStep = getCurrentStep();

	const setPurpose = (purpose: WhoFor) => {
		dispatch(setWhoFor(purpose));
		// No direct onComplete call here
	};

	const handleGiftChooserSelect = (chooser: "myself" | "recipient") => {
		dispatch(setGiftChooser(chooser));
		// If "myself", set giftType to brick_mosaic and flow will complete automatically via useEffect
		if (chooser === "myself") {
			dispatch(setGiftType("brick_mosaic"));
		}
		// If "recipient", flow will continue to gift type selection
	};

	const handleGiftTypeSelect = (giftType: GiftType) => {
		if (giftType === "gift_card") {
			// Redirect to Shopify gift card page immediately without updating Redux
			window.location.href = `${websiteUrl}/products/brick-me-gift-card`;
			return;
		}
		// Redirect to Shopify mosaic gift page for brick mosaic
		window.location.href = `${websiteUrl}/pages/mosaic-gift`;
		return;
	};

	const handleGuestLogin = () => {
		dispatch(guestLogin());
		onGuestLogin();
	};

	const handleBack = () => {
		switch (currentStep) {
			case "purpose":
				// Go to website URL instead of browser back
				window.location.href = websiteUrl;
				break;
			case "giftChooser":
				// Go back to purpose selection by clearing whoFor
				dispatch(clearWhoFor());
				break;
			case "giftType":
				// Go back to gift chooser step by clearing giftType and giftChooser
				dispatch(clearGiftType());
				dispatch(clearGiftChooser());
				break;
			case "name":
				// If we have a gift type, go back to gift type selection
				// If we have a gift chooser (but no gift type), go back to gift chooser
				// Otherwise go back to purpose selection
				if (giftType) {
					// If giftChooser is "myself", we came from "I'll choose" path
					// Clear both giftType and giftChooser to go back to gift chooser step
					if (giftChooser === "myself") {
						dispatch(clearGiftType());
						dispatch(clearGiftChooser());
					} else {
						// Otherwise, just clear giftType to go back to gift type selection
						dispatch(clearGiftType());
					}
				} else if (giftChooser) {
					dispatch(clearGiftChooser());
				} else {
					dispatch(clearWhoFor());
				}
				break;
			case "email":
				// If we're in OTP verification, go back to email input
				// Otherwise go back to name collection
				if (user.type === "waitingForOtp" || user.type === "verifyingOtp") {
					dispatch(changeEmailAddress());
				} else {
					dispatch(clearParsedNameValues());
				}
				break;
			default:
				ensureExhaustive(currentStep);
		}
	};

	const renderCurrentStep = () => {
		switch (currentStep) {
			case "purpose":
				return <PurposeSelectionStep onPurposeSelect={setPurpose} />;
			case "giftChooser":
				return (
					<GiftChooserSelectionStep
						onGiftChooserSelect={handleGiftChooserSelect}
					/>
				);
			case "giftType":
				return (
					<GiftTypeSelectionStep onGiftTypeSelect={handleGiftTypeSelect} />
				);
			case "name":
				if (skipNameAndEmail) return null;
				return <NameCollectionStep />;
			case "email":
				if (skipNameAndEmail) return null;
				return (
					<EmailCollectionStep
						previouslyCollectedName={
							parsedNameValues
								? `${parsedNameValues.firstName} ${parsedNameValues.middleName || ""} ${parsedNameValues.lastName || ""}`.trim()
								: undefined
						}
						onGuestLogin={handleGuestLogin}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<>
			<BackButton onBack={handleBack} show={true} />
			{renderCurrentStep()}
		</>
	);
}

export default GetStartedFlow;
